import React, { useEffect, useRef } from "react";
import { useBettingEventWithMarkets } from "@/hooks/firestore/betting/useEvent";
import { isEmpty } from "lodash";
import { useToggleSelection } from "@/features/betslip/hooks/useToggleSelection";
import { useDockActions } from "@/features/shell/hooks/useDock";
import type { PickslipPick } from "@/features/pickems/types/PickslipPick";

type AddSportPicksToBetslipProps = {
  picks: PickslipPick[];
  eventId: string;
  entryId: string;
};

const AddSportsPicksToBetslip = ({
  picks,
  eventId,
  entryId,
}: AddSportPicksToBetslipProps) => {
  const { event, isLoading: isEventLoading } = useBettingEventWithMarkets(
    eventId,
    "all",
  );
  const { toggle } = useToggleSelection();
  const { openDock } = useDockActions();
  const hasAdded = useRef(false);

  useEffect(() => {
    if (hasAdded.current) return;
    if (!event || isEventLoading) return;

    picks.forEach((pick) => {
      const mainMarket = event.mainMarket;
      if (
        !mainMarket ||
        mainMarket.id !== pick.bettingMarketId ||
        isEmpty(mainMarket.outcomes)
      ) {
        return;
      }

      // find the outcomeId of the betting outcome counterpart;
      // this will be removed when backend adds the `bettingOutcomeId`
      // property
      const bettingOutcome = Object.entries(mainMarket.outcomes).flatMap(
        ([outcomeId, outcome]) =>
          outcome.competitorId === pick.competitor?.id &&
          outcome.type === pick.outcome?.type
            ? { ...outcome, id: outcomeId }
            : [],
      )?.[0];
      if (!bettingOutcome) return;

      toggle({
        kind: "match",
        event,
        market: mainMarket,
        outcome: bettingOutcome,
        outcomeId: bettingOutcome.id,
        hub: "sports",
        originType: "PICKEMS",
        originId: entryId,
      });
    });

    openDock("betslip");
    hasAdded.current = true;
  }, [event, picks, isEventLoading, toggle, entryId, openDock]);

  return <></>;
};

export { AddSportsPicksToBetslip };
